import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAgentsApi } from '@/hooks/useAgentsApi';
import { useApi } from '@/hooks/useApi';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Building2,
  ChevronLeft,
  ChevronRight,
  Eye,
  Loader2,
  Search,
  ThumbsUp,
  Users,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const statusOptions = [
  { value: '', label: 'All Statuses' },
  { value: 'active', label: 'Active' },
  { value: 'pending', label: 'Pending' },
  { value: 'past_due', label: 'Past Due' },
  { value: 'cancelled', label: 'Cancelled' },
];

const statusColors: Record<string, string> = {
  active: 'bg-green-500/10 text-green-500 border-green-500/20',
  pending: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
  past_due: 'bg-orange-500/10 text-orange-500 border-orange-500/20',
  cancelled: 'bg-red-500/10 text-red-500 border-red-500/20',
};

export default function AgentAffiliates() {
  const agentsApi = useAgentsApi();
  const { api } = useApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [cursor, setCursor] = useState<string | null>(null);
  const [cursorHistory, setCursorHistory] = useState<(string | null)[]>([null]);
  const [currentPage, setCurrentPage] = useState(0);
  const [recommendingId, setRecommendingId] = useState<string | null>(null);
  const limit = 10;

  const handleRecommend = async (affiliateId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setRecommendingId(affiliateId);
    try {
      const response = await api.patch(`/agents/affiliates/${affiliateId}/recommend`, {});
      if (!response.success) throw new Error(response.error);
      toast.success('Recommendation sent to HQ');
      queryClient.invalidateQueries({ queryKey: ['agent-affiliates'] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to recommend');
    } finally {
      setRecommendingId(null);
    }
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['agent-affiliates', statusFilter, cursor],
    queryFn: async () => {
      const response = await agentsApi.getAffiliates({
        status: statusFilter || undefined,
        cursor: cursor || undefined,
        limit,
      });
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
  });

  // Client-side search filtering
  const filteredAffiliates = useMemo(() => {
    if (!data?.affiliates || !searchQuery) return data?.affiliates || [];
    const query = searchQuery.toLowerCase();
    return data.affiliates.filter((affiliate) => affiliate.name.toLowerCase().includes(query));
  }, [data?.affiliates, searchQuery]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const goToNextPage = () => {
    if (data?.pagination?.next_cursor) {
      setCursorHistory((prev) => [...prev, data.pagination.next_cursor]);
      setCursor(data.pagination.next_cursor);
      setCurrentPage((prev) => prev + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 0) {
      const newHistory = [...cursorHistory];
      newHistory.pop();
      setCursorHistory(newHistory);
      setCursor(newHistory[newHistory.length - 1] ?? null);
      setCurrentPage((prev) => prev - 1);
    }
  };

  const resetPagination = () => {
    setCursor(null);
    setCursorHistory([null]);
    setCurrentPage(0);
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex h-[50vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="flex h-[50vh] flex-col items-center justify-center gap-4">
          <p className="text-destructive">Failed to load affiliates</p>
          <Button variant="outline" onClick={() => window.location.reload()}>
            Retry
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gradient-gold">My Affiliates</h1>
        <p className="mt-1 text-muted-foreground">
          View and manage all gyms signed in your territory.
        </p>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="py-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search affiliates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="w-full sm:w-48">
              <Select
                options={statusOptions}
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  resetPagination();
                }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" />
            Affiliates ({data?.total_count || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredAffiliates.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="rounded-full bg-muted p-4 mb-4">
                <Building2 className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-1">No affiliates yet</h3>
              <p className="text-muted-foreground max-w-md">
                {searchQuery || statusFilter
                  ? 'No affiliates match your search criteria.'
                  : 'Share your referral link to start signing gyms to your network.'}
              </p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Gym Name</TableHead>
                    <TableHead>Owner</TableHead>
                    <TableHead>Members</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAffiliates.map((affiliate, index) => (
                    <TableRow
                      key={affiliate.id}
                      className="animate-fade-in cursor-pointer hover:bg-muted/50"
                      style={{ animationDelay: `${index * 30}ms` }}
                      onClick={() => navigate(`/agent/affiliates/${affiliate.id}`)}
                    >
                      <TableCell className="font-medium">{affiliate.name}</TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div>{affiliate.owner?.name || '-'}</div>
                          <div className="text-muted-foreground text-xs">
                            {affiliate.owner?.email || ''}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="flex items-center gap-1 text-muted-foreground">
                          <Users className="h-3.5 w-3.5" />
                          {affiliate.member_count ?? 0}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={statusColors[affiliate.subscription_status] || ''}
                        >
                          {affiliate.subscription_status === 'active' &&
                          !affiliate.stripe_subscription_id
                            ? 'Approved (No Payment)'
                            : affiliate.subscription_status.charAt(0).toUpperCase() +
                              affiliate.subscription_status.slice(1).replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDate(affiliate.join_date)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/agent/affiliates/${affiliate.id}`);
                            }}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {affiliate.subscription_status === 'pending' && (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={recommendingId === affiliate.id}
                              onClick={(e) => handleRecommend(affiliate.id, e)}
                            >
                              {recommendingId === affiliate.id ? (
                                <Loader2 className="h-4 w-4 animate-spin mr-1" />
                              ) : (
                                <ThumbsUp className="h-4 w-4 mr-1" />
                              )}
                              Recommend
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {(data?.pagination?.has_more || currentPage > 0) && (
                <div className="flex items-center justify-between border-t border-border pt-4 mt-4">
                  <p className="text-sm text-muted-foreground">
                    Showing {currentPage * limit + 1} to{' '}
                    {currentPage * limit + (filteredAffiliates?.length || 0)} of{' '}
                    {data?.total_count || 0}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={currentPage === 0}
                      onClick={goToPreviousPage}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm">Page {currentPage + 1}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={!data?.pagination?.has_more}
                      onClick={goToNextPage}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </DashboardLayout>
  );
}
